import {
  EventDTO,
  HotDTO,
  ResultDataPointDTO,
  SloDTO,
  SloPerformanceDTO,
} from '@atlassiansox/phobos-client-ts';

import {
  checkTimestampHasSameDayAsDate,
  getStartOfDayUTC,
} from '@/controllers/slo-graph-controller/utils';

import { SloEvents } from './api';

export type FormPayload<T = {}> = {
  name: string;
  description?: string;
} & T;

type withPublicId = {
  publicId: string;
};

/**
 * Linking using the default API entity model,
 * used for things such as <Tag/> as well as "downstreamDependencies" & "upstreamDependencies".
 */
export type EntityLink = {
  name: string;
  href?: string;
} & withPublicId;

export type SLOCrudPayload = FormPayload<{
  type: FieldOption<API.SLOTypeName>;
  runbookUrl?: string;
  lowTrafficThreshold?: number;
  suppressLowTrafficAlerts?: boolean;
  isUltraSteepBurnEnabled?: boolean;
  serviceId?: FieldOption<string> | string | null;
  objective: number | string;
  capability: FieldOption<string> & withPublicId;
  goodQuery: string;
  badQuery: string;
  isDraft: boolean;
  uiMetrics?: any; // FIXME: Cleanup
  manualTag: { [key: string]: string | undefined };
  serviceIdCW: string;
  namespace: string;
  environment: string;
  rollupType: string;
  resourceName: string;
  successExpression: string;
  failureExpression: string;
}>;

export type SloIncidents = (HotDTO & API.HOT)[];

export type DataPoint = {
  timestamp: number;
  goodCount?: number;
  badCount?: number;
  result?: number;
  isHighCardinality?: boolean;
  isLowTraffic?: boolean;
};
export type DataPointWithEvents = DataPoint & {
  events: EventDTO[];
  incidents: SloIncidents;
};

export type DataPointsEventsTotals = DataPointWithEvents & {
  dailyGood?: number;
  dailyBad?: number;
  totalCount?: number;
};

export type ResultDataPointDTOWithEvents = ResultDataPointDTO & {
  events: EventDTO[];
  incidents: SloIncidents;
};

export type PerformanceWithEvents = {
  [k in keyof SloPerformanceDTO]: k extends 'dataPoints'
    ? ResultDataPointDTOWithEvents[]
    : SloPerformanceDTO[k];
};
export type SLO = {
  [k in keyof SloDTO]: k extends 'performance' ? PerformanceWithEvents : SloDTO[k];
} & {
  isSkeleton?: boolean;
};

export type Capability = FormPayload<API.Capability>;

export const joinEventsToPerformance = (
  events: SloEvents,
  incidents: SloIncidents,
  perf: SloPerformanceDTO
): PerformanceWithEvents => ({
  ...perf,
  dataPoints: perf.dataPoints.map(d => {
    // Find and attach daily SLI incidents
    const sameDayIncidents: SloIncidents = incidents.filter(({ impactStart = 0 }) =>
      checkTimestampHasSameDayAsDate(d.timestamp, getStartOfDayUTC(new Date(impactStart)))
    );

    return {
      ...d,
      events: events[new Date(d.timestamp).getTime()] || [],
      incidents: sameDayIncidents,
    };
  }),
});

export const joinEventsToSlo = (
  events: SloEvents,
  incidents: SloIncidents,
  sloDTO: SloDTO
): SLO => ({
  ...sloDTO,
  performance: sloDTO.performance && joinEventsToPerformance(events, incidents, sloDTO.performance),
});
